import { askConfirm } from '@/components/ui/ConfirmHost';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getResultTemplates, postResultTemplate, putResultTemplate, deleteResultTemplate,
  getResultSignatories, getAssessmentComponents, getGradingScales,
  getClasses, getSubjects, getProfile, putProfile, getResultsPreviewSample,
} from '@/api/owner';
import {
  Plus, X, Edit2, Trash2, FileText, AlertCircle, Save, Eye, EyeOff,
  Image as ImageIcon, Upload, User, BookOpen, Layers,
} from 'lucide-react';
import { resolveStorageUrl } from '@/utils/storage';
import { uploadMedia } from '@/utils/upload';
import { isNativeApp } from '@/lib/native';

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};

interface TemplateConfig {
  signatory_ids: number[];
  footer_text: string;
  stamp_path: string | null;
  logo_position: 'left' | 'center' | 'right';
  show_bio: boolean;
  show_position: boolean;
  show_average: boolean;
  show_grade: boolean;
  show_remarks: boolean;
  show_teacher_remark: boolean;
  show_head_remark: boolean;
  column_codes: string[];
  subject_ids: number[];
  stamp_position: { x: number; y: number; width: number } | null;
}

const defaultConfig: TemplateConfig = {
  signatory_ids: [],
  footer_text: '',
  stamp_path: null,
  logo_position: 'left',
  show_bio: true,
  show_position: true,
  show_average: true,
  show_grade: true,
  show_remarks: true,
  show_teacher_remark: true,
  show_head_remark: true,
  column_codes: [],
  subject_ids: [],
  stamp_position: null,
};

interface TemplateForm {
  id?: number;
  name: string;
  type: 'termly' | 'annual' | 'combined';
  /** the classes this sheet is for; empty = all classes */
  class_ids: number[];
  config: TemplateConfig;
}

const emptyForm: TemplateForm = {
  name: '',
  type: 'termly',
  class_ids: [],
  config: { ...defaultConfig },
};

const SAMPLE_STUDENT = {
  name: 'Ada Okafor', admission_no: 'OJP/26/0043', class_name: 'JSS 1 A',
  dob: '14 Mar 2015', gender: 'Female',
};

export function SheetDesignerPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState<TemplateForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(true);
  const [previewReal, setPreviewReal] = useState(false);

  // School identity (edited alongside template)
  const [schoolName, setSchoolName] = useState('');
  const [schoolMotto, setSchoolMotto] = useState('');
  const [logoPath, setLogoPath] = useState<string | null>(null);
  const [schoolDirty, setSchoolDirty] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingStamp, setUploadingStamp] = useState(false);
  const logoRef = useRef<HTMLInputElement>(null);
  const stampRef = useRef<HTMLInputElement>(null);

  // ── Queries ──
  const templatesQ = useQuery({
    queryKey: ['owner-result-templates'],
    queryFn: () => getResultTemplates().then((r: any) => r.data?.data ?? r.data),
  });
  const signatoriesQ = useQuery({
    queryKey: ['result-signatories'],
    queryFn: () => getResultSignatories().then((r: any) => r.data?.data ?? r.data),
  });
  // every column set (all-classes and per-class); the sheet shows the columns of the classes it is for
  const sheetClasses = form?.class_ids ?? [];
  const sheetClass = sheetClasses[0] ?? null;
  const componentsQ = useQuery({
    queryKey: ['assessment-components', 'sheet-all'],
    queryFn: () => getAssessmentComponents({}).then((r: any) => r.data?.data ?? r.data),
  });
  const scalesQ = useQuery({
    queryKey: ['grading-scales'],
    queryFn: () => getGradingScales().then((r: any) => r.data?.data ?? r.data),
  });
  const classesQ = useQuery({
    queryKey: ['owner-classes'],
    queryFn: () => getClasses({ per_page: 200 }).then((r: any) => r.data?.data ?? r.data),
  });
  const subjectsQ = useQuery({
    queryKey: ['owner-subjects'],
    queryFn: () => getSubjects({ per_page: 500 }).then((r: any) => r.data?.data ?? r.data),
  });
  const profileQ = useQuery({
    queryKey: ['owner-profile'],
    queryFn: () => getProfile().then((r: any) => r.data?.data ?? r.data),
  });

  const templates = arr<any>(templatesQ.data);
  const signatories = arr<any>(signatoriesQ.data);
  const allComponents = arr<any>((componentsQ.data as any)?.components ?? componentsQ.data);
  // the chosen classes' own columns (plus the all-classes set for any chosen class without its own); all classes: every column
  const components = (() => {
    const general = allComponents.filter((c: any) => !(c.class_ids ?? []).length);
    const ownFor = (id: number) => allComponents.filter((c: any) => (c.class_ids ?? []).map(Number).includes(id));
    const picked = sheetClasses.length
      ? sheetClasses.flatMap((id) => { const own = ownFor(id); return own.length ? own : general; })
      : allComponents;
    const seen = new Set<string>();
    return picked.filter((c: any) => (seen.has(c.code) ? false : (seen.add(c.code), true)));
  })();
  const scales = arr<any>(scalesQ.data);
  const classes = arr<any>(classesQ.data);
  const subjects = arr<any>(subjectsQ.data);
  const profile = profileQ.data;

  const previewSampleQ = useQuery({
    queryKey: ['results-preview-sample', sheetClass],
    queryFn: () => getResultsPreviewSample({ class_id: sheetClass ?? undefined }).then((r: any) => r.data?.data ?? r.data),
    enabled: !!form && previewReal,
  });
  const previewSample = previewSampleQ.data;

  // Hydrate school identity once profile loads
  useEffect(() => {
    if (profile) {
      setSchoolName(profile.name ?? '');
      setSchoolMotto(profile.motto ?? '');
      setLogoPath(profile.logo_path ?? null);
    }
  }, [profile]);

  // A different class can have different columns: keep the picked ones that exist, otherwise show all of the class's columns
  useEffect(() => {
    if (!form || !components.length) return;
    const codes = components.map((c: any) => c.code);
    const kept = form.config.column_codes.filter((c: string) => codes.includes(c));
    if (kept.length !== form.config.column_codes.length) setForm({ ...form, config: { ...form.config, column_codes: kept.length ? kept : codes } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetClasses.join(','), componentsQ.data]);

  // Seed default column_codes from components when opening new template
  useEffect(() => {
    if (form && form.config.column_codes.length === 0 && components.length > 0) {
      setForm({
        ...form,
        config: { ...form.config, column_codes: components.map((c: any) => c.code) },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [components.length, form === null]);

  const activeScale = scales.find((s: any) => s.is_default) ?? scales[0];
  const gradeFor = (score: number) => {
    const item = (activeScale?.items ?? []).find((i: any) => score >= i.min_score && score <= i.max_score);
    return item?.grade ?? '—';
  };

  // ── Mutations ──
  const saveM = useMutation({
    mutationFn: async () => {
      if (!form) throw new Error('No form');

      // Save school identity first if changed
      if (schoolDirty) {
        await putProfile({
          name: schoolName.trim(),
          motto: schoolMotto.trim() || null,
          logo_path: logoPath,
        });
        qc.invalidateQueries({ queryKey: ['owner-profile'] });
      }

      const body = {
        name: form.name.trim(),
        type: form.type,
        class_ids: form.class_ids,
        columns: form.config.column_codes,
        config: form.config,
      };
      return form.id ? putResultTemplate(form.id, body) : postResultTemplate(body);
    },
    onSuccess: (res: any) => {
      const saved = res?.data?.data ?? res?.data;
      const sigIds = saved?.config?.signatory_ids ?? form?.config.signatory_ids ?? [];
      const cols = saved?.config?.column_codes ?? form?.config.column_codes ?? [];
      const ids: number[] = saved?.class_ids ?? form?.class_ids ?? [];
      const cls = ids.length ? ids.map((id) => { const c = classes.find((x: any) => x.id === id); return c ? `${c.name}${c.arm ? ' ' + c.arm : ''}` : `#${id}`; }).join(', ') : 'all classes';
      alert(
        `Template saved\n\nName: ${saved?.name ?? form?.name}\n` +
        `Scope: ${cls}\n` +
        `Columns: ${cols.length ? cols.join(', ') : '(none)'}\n` +
        `Signatories: ${sigIds.length ? sigIds.length + ' selected' : 'none'}`
      );
      qc.invalidateQueries({ queryKey: ['owner-result-templates'] });
      closeForm();
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? e?.message ?? 'Save failed'),
  });

  const deleteM = useMutation({
    mutationFn: (id: number) => deleteResultTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['owner-result-templates'] }),
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Delete failed'),
  });

  // ── Form helpers ──
  const closeForm = () => {
    setForm(null); setError(null); setSchoolDirty(false);
    if (profile) {
      setSchoolName(profile.name ?? '');
      setSchoolMotto(profile.motto ?? '');
      setLogoPath(profile.logo_path ?? null);
    }
  };

  const openNew = () => {
    setForm({ ...emptyForm, config: { ...defaultConfig } });
    setError(null);
    setSchoolDirty(false);
  };

  const openEdit = (t: any) => {
    setForm({
      id: t.id,
      name: t.name ?? '',
      type: t.type ?? 'termly',
      class_ids: (t.class_ids ?? (t.class_id ? [t.class_id] : [])).map(Number),
      config: { ...defaultConfig, ...(t.config ?? {}) },
    });
    setError(null);
    setSchoolDirty(false);
  };

  const patchConfig = (patch: Partial<TemplateConfig>) => {
    if (!form) return;
    setForm({ ...form, config: { ...form.config, ...patch } });
  };

  const toggleSignatory = (id: number) => {
    if (!form) return;
    const next = form.config.signatory_ids.includes(id)
      ? form.config.signatory_ids.filter(x => x !== id)
      : [...form.config.signatory_ids, id];
    patchConfig({ signatory_ids: next });
  };

  const moveSignatory = (id: number, dir: -1 | 1) => {
    if (!form) return;
    const list = [...form.config.signatory_ids];
    const i = list.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    patchConfig({ signatory_ids: list });
  };

  const toggleSubject = (id: number) => {
    if (!form) return;
    const next = form.config.subject_ids.includes(id)
      ? form.config.subject_ids.filter(x => x !== id)
      : [...form.config.subject_ids, id];
    patchConfig({ subject_ids: next });
  };

  const toggleColumn = (code: string) => {
    if (!form) return;
    const next = form.config.column_codes.includes(code)
      ? form.config.column_codes.filter(c => c !== code)
      : [...form.config.column_codes, code];
    patchConfig({ column_codes: next });
  };

  const handleLogoUpload = async (file: File | null) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const path = await uploadMedia(file);
      setLogoPath(path);
      setSchoolDirty(true);
    } catch (e: any) {
      alert(e?.message ?? 'Logo upload failed');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleStampUpload = async (file: File | null) => {
    if (!file || !form) return;
    setUploadingStamp(true);
    try {
      const path = await uploadMedia(file);
      patchConfig({ stamp_path: path });
    } catch (e: any) {
      alert(e?.message ?? 'Stamp upload failed');
    } finally {
      setUploadingStamp(false);
    }
  };

  const handlePrint = () => {
    const el = document.getElementById('sheet-preview');
    if (!el) return;
    if (isNativeApp) { alert('Printing is not available in the phone app. Export the results to PDF, or print from the website on a computer.'); return; }
    const win = window.open('', '_blank', 'width=800,height=1100');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>Result Sheet</title>
      <style>
        @page { size: A4; margin: 12mm; }
        body { margin: 0; font-family: Georgia, serif; color: #0f172a; }
        table { border-collapse: collapse; width: 100%; }
        th, td { padding: 3px 5px; }
      </style>
    </head><body>${el.innerHTML}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 300);
  };

  const startStampDrag = (e: React.MouseEvent | React.TouchEvent) => {
    if (!form) return;
    e.preventDefault();
    const preview = document.getElementById('sheet-preview');
    if (!preview) return;
    const rect = preview.getBoundingClientRect();
    const move = (clientX: number, clientY: number) => {
      const x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
      const w = form.config.stamp_position?.width ?? 15;
      setForm({ ...form, config: { ...form.config, stamp_position: { x, y, width: w } } });
    };
    const onMouseMove = (ev: MouseEvent) => move(ev.clientX, ev.clientY);
    const onTouchMove = (ev: TouchEvent) => {
      if (ev.touches[0]) move(ev.touches[0].clientX, ev.touches[0].clientY);
    };
    const cleanup = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', cleanup);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', cleanup);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', cleanup);
    window.addEventListener('touchmove', onTouchMove);
    window.addEventListener('touchend', cleanup);
  };

  const submitForm = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form) return;
    if (!form.name.trim()) { setError('Template name is required.'); return; }
    if (form.config.column_codes.length === 0) { setError('Select at least one column.'); return; }
    saveM.mutate();
  };

  // Subjects shown in preview: if config lists specific ones, use those; else all
  const previewSubjects = useMemo(() => {
    if (form?.config.subject_ids?.length) {
      return subjects.filter((s: any) => form.config.subject_ids.includes(s.id));
    }
    return subjects.slice(0, 6);
  }, [form?.config.subject_ids, subjects]);

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Results</p>
          <h1 className="text-3xl font-extrabold text-slate-900">Sheet Designer</h1>
          <p className="text-slate-500 text-sm mt-1">
            Design what prints on the result sheet — school identity, sections, signatories
          </p>
        </div>
        <button onClick={openNew}
          className="px-4 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> New template
        </button>
      </div>

      {templatesQ.isLoading ? (
        <p className="text-sm text-slate-500 py-8 text-center">Loading…</p>
      ) : templates.length === 0 ? (
        <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="font-semibold text-slate-700">No template yet</p>
          <p className="text-sm text-slate-500 mt-1">Create your first result sheet template.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t: any) => {
            const ids: number[] = (t.class_ids ?? (t.class_id ? [t.class_id] : [])).map(Number);
            const names = ids.map((id) => classes.find((c: any) => c.id === id)).filter(Boolean).map((c: any) => `${c.name}${c.arm ? ' ' + c.arm : ''}`);
            const scopeLabel = !ids.length ? 'All classes' : names.length <= 3 ? names.join(' · ') : `${names.slice(0, 2).join(' · ')} +${names.length - 2} more`;
            return (
              <div key={t.id} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0">
                    <h3 className="font-bold text-slate-900 text-lg truncate">{t.name}</h3>
                    <p className="text-xs text-slate-500 capitalize">{t.type}</p>
                    <span className={`mt-1 inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      ids.length ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-slate-100 text-slate-600'
                    }`}>{scopeLabel}</span>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => openEdit(t)} className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={async () => { if (await askConfirm(`Delete "${t.name}"?`)) deleteM.mutate(t.id); }}
                      className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {form && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto">
          <div className="min-h-screen flex items-start justify-center p-4">
            <div className="bg-white w-full max-w-[1400px] rounded-2xl shadow-2xl my-4">
              <form onSubmit={submitForm}>
                <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl sticky top-0 z-10">
                  <div>
                    <h3 className="font-bold text-slate-900 text-lg">{form.id ? 'Edit template' : 'New template'}</h3>
                    <p className="text-xs text-slate-500">Everything saves together</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setShowPreview(v => !v)}
                      className="px-3 py-1.5 text-xs font-semibold border border-slate-300 rounded-lg hover:bg-white flex items-center gap-1">
                      {showPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      {showPreview ? 'Hide preview' : 'Show preview'}
                    </button>
                    <button type="button" onClick={closeForm} className="text-slate-400 hover:text-slate-600">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className={`grid ${showPreview ? 'lg:grid-cols-[460px_1fr]' : 'grid-cols-1'}`}>
                  {/* ─────── LEFT: FORM ─────── */}
                  <div className="p-6 space-y-5 border-r border-slate-200">

                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
                      </div>
                    )}

                    {/* ── SCHOOL IDENTITY ── */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                      <p className="text-xs font-bold text-blue-900 uppercase tracking-wider">School identity (shared)</p>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">School name</label>
                        <input value={schoolName}
                          onChange={(e) => { setSchoolName(e.target.value); setSchoolDirty(true); }}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Motto</label>
                        <input value={schoolMotto}
                          onChange={(e) => { setSchoolMotto(e.target.value); setSchoolDirty(true); }}
                          placeholder="e.g. Knowledge and Character"
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Logo</label>
                        <div className="flex items-center gap-3">
                          {logoPath ? (
                            <img src={resolveStorageUrl(logoPath)} alt="logo" className="h-10 object-contain" />
                          ) : (
                            <div className="w-10 h-10 bg-slate-200 rounded flex items-center justify-center">
                              <ImageIcon className="w-4 h-4 text-slate-400" />
                            </div>
                          )}
                          <button type="button" onClick={() => logoRef.current?.click()} disabled={uploadingLogo}
                            className="text-xs font-semibold text-blue-700 hover:underline disabled:opacity-50">
                            {uploadingLogo ? 'Uploading…' : logoPath ? 'Replace logo' : 'Upload logo'}
                          </button>
                          {logoPath && (
                            <button type="button" onClick={() => { setLogoPath(null); setSchoolDirty(true); }}
                              className="text-xs font-semibold text-red-600 hover:underline">
                              Remove
                            </button>
                          )}
                          <input ref={logoRef} type="file" accept="image/*" className="hidden"
                            onChange={(e) => handleLogoUpload(e.target.files?.[0] ?? null)} />
                        </div>
                      </div>
                    </div>

                    {/* ── TEMPLATE BASICS ── */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Template name *</label>
                      <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        required placeholder="e.g. Standard Secondary Template"
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Type</label>
                        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as any })}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                          <option value="termly">Termly</option>
                          <option value="annual">Annual</option>
                          <option value="combined">Combined</option>
                        </select>
                      </div>
                    </div>
                    <ClassPicker classes={classes} value={form.class_ids} onChange={(ids) => setForm({ ...form, class_ids: ids })} />

                    {/* ── COLUMNS from assessment-components ── */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1 flex items-center gap-1">
                        <Layers className="w-3.5 h-3.5" /> Result columns
                      </label>
                      <p className="text-xs text-slate-500 mb-2">Pulled from <b>Grading &amp; Weights</b>{sheetClasses.length ? ` — the columns of the class${sheetClasses.length > 1 ? 'es' : ''} chosen above` : ' — every class’s columns'}. Pick which columns print on this sheet.</p>
                      {components.length === 0 ? (
                        <p className="text-xs text-amber-700 italic">No columns configured yet — add them on the Grading &amp; Weights page.</p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {components.map((c: any) => {
                            const active = form.config.column_codes.includes(c.code);
                            return (
                              <button type="button" key={c.id} onClick={() => toggleColumn(c.code)}
                                className={`px-2.5 py-1 text-xs font-bold rounded-full border ${
                                  active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-300 hover:border-slate-500'
                                }`}>
                                {c.code} <span className="font-normal opacity-70">{c.max_score}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* ── SUBJECTS ── */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1 flex items-center gap-1">
                        <BookOpen className="w-3.5 h-3.5" /> Subjects on sheet
                      </label>
                      <p className="text-xs text-slate-500 mb-2">Leave empty to include all subjects of the class.</p>
                      {subjects.length === 0 ? (
                        <p className="text-xs text-amber-700 italic">No subjects exist yet.</p>
                      ) : (
                        <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1">
                          {subjects.map((s: any) => {
                            const active = form.config.subject_ids.includes(s.id);
                            return (
                              <label key={s.id} className="flex items-center gap-2 text-sm px-2 py-1 rounded hover:bg-slate-50 cursor-pointer">
                                <input type="checkbox" checked={active} onChange={() => toggleSubject(s.id)} className="w-4 h-4" />
                                <span className="text-slate-700">{s.name}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* ── SIGNATORIES ── */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1">
                        <User className="w-3.5 h-3.5" /> Signatories
                        {form.config.signatory_ids.length > 0 && (
                          <span className="ml-2 text-[10px] font-bold bg-green-100 text-green-700 rounded-full px-2 py-0.5">
                            {form.config.signatory_ids.length} saved
                          </span>
                        )}
                      </label>
                      {signatories.length === 0 ? (
                        <p className="text-xs text-amber-700 italic">
                          No signatories yet. Add them on the <b>Results → Signatories</b> page.
                        </p>
                      ) : (
                        <>
                          <p className="text-xs text-slate-500 mb-2">
                            Check as many as you need — order matches the sheet (top → bottom = left → right).
                            {form.config.signatory_ids.length > 0 && (
                              <b className="ml-1 text-slate-700">{form.config.signatory_ids.length} selected</b>
                            )}
                          </p>
                          <div className="space-y-1">
                            {signatories.map((s: any) => {
                              const checked = form.config.signatory_ids.includes(s.id);
                              const order = checked ? form.config.signatory_ids.indexOf(s.id) + 1 : null;
                              return (
                                <div key={s.id} className={`flex items-center gap-2 px-2 py-1.5 rounded ${checked ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50'}`}>
                                  <input type="checkbox" checked={checked}
                                    onChange={() => toggleSignatory(s.id)} className="w-4 h-4" />
                                  {checked && (
                                    <span className="text-[10px] font-bold bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                                      {order}
                                    </span>
                                  )}
                                  <span className="text-sm font-semibold text-slate-900 truncate">{s.name}</span>
                                  <span className="text-xs text-slate-500 truncate">{s.role}</span>
                                  {s.signature_path && (
                                    <img src={resolveStorageUrl(s.signature_path)} alt="sig"
                                      className="h-6 ml-auto object-contain opacity-70 flex-shrink-0"
                                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                  )}
                                  {checked && (
                                    <div className="flex flex-col ml-1">
                                      <button type="button" onClick={() => moveSignatory(s.id, -1)}
                                        disabled={order === 1}
                                        className="text-slate-400 hover:text-slate-700 disabled:opacity-30 text-[10px] leading-none">▲</button>
                                      <button type="button" onClick={() => moveSignatory(s.id, 1)}
                                        disabled={order === form.config.signatory_ids.length}
                                        className="text-slate-400 hover:text-slate-700 disabled:opacity-30 text-[10px] leading-none">▼</button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>

                    {/* ── SECTION TOGGLES ── */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Sections to include</label>
                      <div className="space-y-1">
                        {([
                          ['show_bio', 'Student bio'],
                          ['show_position', 'Subject positions'],
                          ['show_average', 'Average & total'],
                          ['show_grade', 'Overall grade'],
                          ['show_remarks', 'Per-subject remarks'],
                          ['show_teacher_remark', "Class teacher's remark"],
                          ['show_head_remark', "Head teacher's remark"],
                        ] as const).map(([key, label]) => (
                          <label key={key} className="flex items-center gap-2 text-sm cursor-pointer py-0.5">
                            <input type="checkbox" checked={form.config[key] as boolean}
                              onChange={(e) => patchConfig({ [key]: e.target.checked } as any)}
                              className="w-4 h-4" />
                            <span className="text-slate-700">{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Logo position</label>
                      <select value={form.config.logo_position}
                        onChange={(e) => patchConfig({ logo_position: e.target.value as any })}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">School stamp</label>
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-3 text-center">
                        {form.config.stamp_path ? (
                          <div>
                            <img src={resolveStorageUrl(form.config.stamp_path)} alt="stamp" className="h-16 mx-auto mb-1" />
                            <button type="button" onClick={() => patchConfig({ stamp_path: null })}
                              className="text-xs text-red-600 underline">Remove</button>
                          </div>
                        ) : (
                          <button type="button" onClick={() => stampRef.current?.click()} disabled={uploadingStamp}
                            className="inline-flex flex-col items-center gap-1 text-slate-500 hover:text-slate-700 disabled:opacity-50">
                            <Upload className="w-5 h-5" />
                            <span className="text-xs font-semibold">{uploadingStamp ? 'Uploading…' : 'Upload stamp'}</span>
                          </button>
                        )}
                        <input ref={stampRef} type="file" accept="image/*" className="hidden"
                          onChange={(e) => handleStampUpload(e.target.files?.[0] ?? null)} />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">Footer text</label>
                      <textarea rows={2} value={form.config.footer_text}
                        onChange={(e) => patchConfig({ footer_text: e.target.value })}
                        placeholder="e.g. This result is computer-generated and does not require a stamp."
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                    </div>

                    {schoolDirty && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                        School name / motto / logo will be saved to your school profile when you save this template.
                      </p>
                    )}
                  </div>

                  {/* ─────── RIGHT: LIVE PREVIEW ─────── */}
                  {showPreview && (
                    <div className="bg-slate-100 p-6">
                      <div className="mx-auto mb-3 flex items-center justify-between" style={{ width: 595 }}>
                        <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                          <input type="checkbox" checked={previewReal}
                            onChange={(e) => setPreviewReal(e.target.checked)}
                            className="w-4 h-4" />
                          Preview with real data
                          {previewSampleQ.isFetching && <span className="text-slate-400">(loading…)</span>}
                        </label>
                        <button type="button" onClick={handlePrint}
                          className="text-xs font-semibold bg-white border border-slate-300 px-3 py-1.5 rounded-lg hover:bg-slate-50">
                          🖨 Print / Save as PDF
                        </button>
                      </div>
                      <div className="mx-auto" style={{ width: 595 }}>
                        <div id="sheet-preview" className="bg-white shadow-2xl p-7 relative" style={{ fontFamily: 'Georgia, serif', color: '#0f172a' }}>
                          {/* Header */}
                          <div className={`flex items-start gap-4 pb-4 border-b-2 border-slate-900 ${
                            form.config.logo_position === 'center' ? 'flex-col items-center text-center' :
                            form.config.logo_position === 'right' ? 'flex-row-reverse text-right' : ''
                          }`}>
                            {logoPath ? (
                              <img src={resolveStorageUrl(logoPath)} alt="logo" className="w-14 h-14 object-contain flex-shrink-0" />
                            ) : (
                              <div className="w-14 h-14 bg-slate-200 rounded flex items-center justify-center text-slate-400 text-[9px] font-bold flex-shrink-0">LOGO</div>
                            )}
                            <div className="flex-1">
                              <p className="text-xl font-bold tracking-wide uppercase">{schoolName || 'School name'}</p>
                              {schoolMotto && <p className="text-xs italic text-slate-600 mt-0.5">{schoolMotto}</p>}
                              <p className="text-xs text-slate-500 mt-1">First Term · 2025/2026 · {SAMPLE_STUDENT.class_name}</p>
                            </div>
                          </div>

                          {/* Bio */}
                          <div className="flex gap-4 py-4">
                            <div className="w-16 h-20 bg-slate-200 rounded flex items-center justify-center text-slate-400 text-[10px] flex-shrink-0">
                              PASSPORT
                            </div>
                            <div className="flex-1 text-sm space-y-1">
                              <div className="flex gap-3"><span className="text-slate-500 w-24">Name</span><span className="font-bold">{SAMPLE_STUDENT.name}</span></div>
                              <div className="flex gap-3"><span className="text-slate-500 w-24">Adm. No.</span><span className="font-mono">{SAMPLE_STUDENT.admission_no}</span></div>
                              {form.config.show_bio && (
                                <div className="flex gap-6 text-xs text-slate-700">
                                  <span><b>DOB:</b> {SAMPLE_STUDENT.dob}</span>
                                  <span><b>Gender:</b> {SAMPLE_STUDENT.gender}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Subjects table */}
                          {form.config.column_codes.length > 0 && (
                            <table className="w-full text-[10px]" style={{ borderCollapse: 'collapse' }}>
                              <thead>
                                <tr className="bg-slate-800 text-white">
                                  <th className="text-left px-2 py-1.5">SUBJECT</th>
                                  {form.config.column_codes.map(code => (
                                    <th key={code} className="px-1 py-1.5 text-center w-9">{code}</th>
                                  ))}
                                  <th className="px-1 py-1.5 text-center w-10">TOT</th>
                                  {form.config.show_grade && <th className="px-1 py-1.5 text-center w-8">GR</th>}
                                  {form.config.show_position && <th className="px-1 py-1.5 text-center w-8">POS</th>}
                                  {form.config.show_remarks && <th className="text-left px-2 py-1.5">REMARK</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {previewSubjects.slice(0, 6).map((subj: any, i: number) => {
                                  const mockScores: Record<string, number> = {};
                                  form.config.column_codes.forEach((code, idx) => {
                                    mockScores[code] = Math.round(6 + (idx * 2 + i) % 5);
                                  });
                                  const maxes = form.config.column_codes.map(code => {
                                    const comp = components.find((c: any) => c.code === code);
                                    return Number(comp?.max_score ?? 10);
                                  });
                                  const total = form.config.column_codes.reduce((sum, code, idx) => {
                                    return sum + (mockScores[code] / (maxes[idx] || 10)) * (maxes[idx] || 10);
                                  }, 0);
                                  const pct = Math.round((total / Math.max(1, maxes.reduce((a, b) => a + b, 0))) * 100);
                                  return (
                                    <tr key={subj.id ?? i} className="border-b border-slate-200">
                                      <td className="px-2 py-1.5">{subj.name}</td>
                                      {form.config.column_codes.map(code => (
                                        <td key={code} className="px-1 py-1.5 text-center">{mockScores[code]}</td>
                                      ))}
                                      <td className="px-1 py-1.5 text-center font-bold">{total}</td>
                                      {form.config.show_grade && <td className="px-1 py-1.5 text-center font-bold">{gradeFor(pct)}</td>}
                                      {form.config.show_position && <td className="px-1 py-1.5 text-center">{i + 1}</td>}
                                      {form.config.show_remarks && <td className="px-2 py-1.5 text-slate-600">Good</td>}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}

                          {/* Summary */}
                          {(form.config.show_average || form.config.show_grade) && (
                            <div className="flex gap-6 justify-end py-3 text-xs border-b border-slate-200">
                              {form.config.show_average && <>
                                <span><b>Total:</b> 283</span>
                                <span><b>Average:</b> 70.75</span>
                              </>}
                              {form.config.show_position && <span><b>Position:</b> 4 of 32</span>}
                              {form.config.show_grade && <span><b>Grade:</b> B2</span>}
                            </div>
                          )}

                          {/* Remarks */}
                          {(form.config.show_teacher_remark || form.config.show_head_remark) && (
                            <div className="py-3 space-y-2 text-xs">
                              {form.config.show_teacher_remark && (
                                <div>
                                  <p className="font-bold text-slate-700">Class Teacher's Remark</p>
                                  <p className="text-slate-600 italic">"Steady progress. Keep practising fractions."</p>
                                </div>
                              )}
                              {form.config.show_head_remark && (
                                <div>
                                  <p className="font-bold text-slate-700">Head Teacher's Remark</p>
                                  <p className="text-slate-600 italic">"A commendable result. Well done."</p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Stamp — draggable when positioned */}
                          {form.config.stamp_path && (
                            form.config.stamp_position ? (
                              <img
                                src={resolveStorageUrl(form.config.stamp_path)}
                                alt="stamp"
                                onMouseDown={startStampDrag}
                                onTouchStart={startStampDrag}
                                className="absolute cursor-move select-none opacity-80"
                                style={{
                                  left: `${form.config.stamp_position.x}%`,
                                  top: `${form.config.stamp_position.y}%`,
                                  width: `${form.config.stamp_position.width}%`,
                                  transform: 'translate(-50%, -50%)',
                                }}
                                title="Drag to reposition"
                              />
                            ) : (
                              <div className="flex justify-center py-2">
                                <div className="flex flex-col items-center gap-1">
                                  <img src={resolveStorageUrl(form.config.stamp_path)} alt="stamp" className="h-16 opacity-80" />
                                  <button
                                    type="button"
                                    onClick={() => patchConfig({ stamp_position: { x: 50, y: 70, width: 15 } })}
                                    className="text-[10px] text-blue-700 hover:underline"
                                  >
                                    Enable drag positioning
                                  </button>
                                </div>
                              </div>
                            )
                          )}

                          {/* Signatures */}
                          {form.config.signatory_ids.length > 0 && (
                            <div className="flex justify-between gap-6 pt-6 mt-2 border-t border-slate-200">
                              {form.config.signatory_ids.map((id) => {
                                const s = signatories.find((x: any) => x.id === id);
                                if (!s) return null;
                                return (
                                  <div key={id} className="flex-1 text-center">
                                    {s.signature_path ? (
                                      <img src={resolveStorageUrl(s.signature_path)} alt="sig"
                                        className="h-10 mx-auto object-contain"
                                        onError={(e) => {
                                          const img = e.target as HTMLImageElement;
                                          img.style.display = 'none';
                                        }} />
                                    ) : <div className="h-10" />}
                                    <div className="border-t border-slate-400 mx-4 mt-1 pt-1">
                                      <p className="text-[10px] font-bold uppercase">{s.name}</p>
                                      <p className="text-[9px] text-slate-500">{s.role}</p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Footer */}
                          {form.config.footer_text && (
                            <p className="text-center text-[9px] text-slate-500 italic mt-4 pt-2 border-t border-slate-200">
                              {form.config.footer_text}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="px-6 py-4 border-t border-slate-200 flex gap-3 bg-slate-50 rounded-b-2xl sticky bottom-0">
                  <button type="button" onClick={closeForm}
                    className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm">
                    Cancel
                  </button>
                  <button type="submit" disabled={saveM.isPending}
                    className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2">
                    <Save className="w-4 h-4" />
                    {saveM.isPending ? 'Saving…' : 'Save template'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Which classes a sheet is for: all, or any mix of classes (quick picks by level). */
function ClassPicker({ classes, value, onChange }: { classes: any[]; value: number[]; onChange: (ids: number[]) => void }) {
  const label = (c: any) => `${c.name}${c.arm ? ' ' + c.arm : ''}`;
  const levels = Array.from(new Set(classes.map((c: any) => c.name)));
  const toggle = (id: number) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const setLevel = (lvl: string) => {
    const ids = classes.filter((c: any) => c.name === lvl).map((c: any) => c.id);
    const all = ids.every((id) => value.includes(id));
    onChange(all ? value.filter((x) => !ids.includes(x)) : Array.from(new Set([...value, ...ids])));
  };
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-1">Applies to</label>
      <div className="rounded-lg border border-slate-300 p-2.5 space-y-2">
        <label className="flex items-center gap-2 text-sm font-semibold">
          <input type="checkbox" checked={value.length === 0} onChange={() => onChange([])} /> All classes
        </label>
        {levels.length > 1 && (
          <div className="flex flex-wrap gap-1.5">
            {levels.map((lvl) => {
              const ids = classes.filter((c: any) => c.name === lvl).map((c: any) => c.id);
              const on = ids.length > 0 && ids.every((id) => value.includes(id));
              return <button type="button" key={lvl} onClick={() => setLevel(lvl)} className={`px-2 py-0.5 text-[11px] font-bold rounded-full border ${on ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-300'}`}>All {lvl}</button>;
            })}
          </div>
        )}
        <div className="grid grid-cols-2 gap-1 max-h-40 overflow-y-auto">
          {classes.map((c: any) => (
            <label key={c.id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={value.includes(c.id)} onChange={() => toggle(c.id)} /> {label(c)}
            </label>
          ))}
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-1">{value.length ? `${value.length} class${value.length > 1 ? 'es' : ''} chosen.` : 'Used for every class that has no sheet of its own.'}</p>
    </div>
  );
}
